import { ElevenLabsClient as ElevenLabs } from '@elevenlabs/elevenlabs-js';
import type { VoiceCategory, VoiceInfo, VoiceSearchResult } from '@/types';
import { DEFAULT_ELEVENLABS_MODEL, VOICE_SEARCH } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { retryWithBackoff } from '@/lib/asyncUtils';
import { getAudioDuration } from '@/lib/audioUtils';

interface GenerateAudioParams {
  text: string;
  voiceId: string;
  apiKey: string;
  stability?: number;
  similarityBoost?: number;
  signal?: AbortSignal;
}

interface GenerateAudioResult {
  blob: Blob;
  duration: number;
}

export class RateLimitError extends Error {
  retryAfter?: number;
  remainingCharacters?: number;

  constructor(
    message: string,
    retryAfter?: number,
    remainingCharacters?: number
  ) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
    this.remainingCharacters = remainingCharacters;
  }
}

function extractRetryAfter(error: Error): number | undefined {
  const match = error.message.match(/retry after (\d+)/i);
  if (match) {
    return parseInt(match[1], 10) * 1000;
  }
  return undefined;
}

function isElevenLabsRateLimitError(error: Error): boolean {
  return (
    error.message.includes('429') ||
    error.message.includes('rate limit') ||
    error.message.includes('quota exceeded')
  );
}

let clientInstance: ElevenLabs | null = null;
let currentApiKey: string | null = null;

function getClient(apiKey: string): ElevenLabs {
  // ElevenLabsクライアントはAPIキーを後から変更できないため再生成が必要
  if (!clientInstance || currentApiKey !== apiKey) {
    clientInstance = new ElevenLabs({ apiKey });
    currentApiKey = apiKey;
  }
  return clientInstance;
}

export async function generateAudio(params: GenerateAudioParams): Promise<GenerateAudioResult> {
  const { text, voiceId, apiKey, stability = 0.5, similarityBoost = 0.75, signal } = params;

  return retryWithBackoff(
    async () => {
      // API呼び出し前にキャンセルチェック
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      logger.debug('ElevenLabs API呼び出し開始', {
        textLength: text.length,
        voiceId: voiceId.substring(0, 8) + '...',
      });

      const client = getClient(apiKey);

      const audioStream = await client.textToSpeech.convert(
        voiceId,
        {
          text,
          modelId: DEFAULT_ELEVENLABS_MODEL,
          voiceSettings: {
            stability,
            similarityBoost,
          },
        },
        {
          timeoutInSeconds: 30,
          maxRetries: 0,
        }
      );

      // ストリーム読み込み前にキャンセルチェック
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const response = new Response(audioStream as unknown as ReadableStream);
      const blob = await response.blob();

      const duration = await getAudioDuration(blob);

      logger.debug('ElevenLabs API呼び出し成功', {
        duration: `${duration.toFixed(2)}秒`,
        blobSize: `${(blob.size / 1024).toFixed(1)}KB`,
      });

      return { blob, duration };
    },
    {
      maxRetries: 3,
      baseDelay: 1000,
      signal,
      isRateLimitError: isElevenLabsRateLimitError,
      getRetryAfter: extractRetryAfter,
      contextName: 'ElevenLabs API',
    }
  );
}

export interface VoiceSearchParams {
  apiKey: string;
  pageToken?: string;
  /** 名前・説明・ラベル・カテゴリを横断する検索語 */
  search?: string;
  category?: VoiceCategory;
}

export async function searchVoices(params: VoiceSearchParams): Promise<VoiceSearchResult> {
  const client = getClient(params.apiKey);
  const response = await client.voices.search({
    pageSize: VOICE_SEARCH.PAGE_SIZE,
    nextPageToken: params.pageToken,
    search: params.search || undefined,
    category: params.category,
  });

  const voices: VoiceInfo[] = response.voices.map((v) => ({
    voiceId: v.voiceId,
    name: v.name ?? '',
    category: v.category as VoiceInfo['category'],
    description: v.description,
    previewUrl: v.previewUrl,
  }));

  return {
    voices,
    hasMore: response.hasMore,
    nextPageToken: response.nextPageToken,
  };
}
